/**
 * DEV-ONLY seed HITL events (CT-5096) — TEMPORARY.
 *
 * The tenant has NO HITL tasks from agent runs unless LangGraph creates them via
 * POST /v1/tasks/hitl. Real pending rows also arrive through hitl/list in the
 * aggregate fan-out (CT-5096). This seed is only for prototype fixtures when you
 * want mock rows WITHOUT contractId (local resolve) on top of live data.
 *
 * These rows have NO `detail.contractId`, so resolving one takes the LOCAL path
 * (the card leaves the inbox with a success toast, nothing is POSTed) — which is
 * the real, honest behavior for a mock with no backend task. A REAL HITL event
 * (with a `contractId` from hitl/list) hit POST /v1/tasks/hitl/resolve instead.
 *
 * Gated to `import.meta.env.DEV` AND an explicit localStorage flag, so it NEVER
 * reaches production and never appears in a normal dev session unless asked for:
 *
 *   Enable :  localStorage.setItem('wq_seed_hitl', '1')   // then reload
 *   Disable:  localStorage.removeItem('wq_seed_hitl')
 *
 * Live HITL rows with a contractId (from hitl/list) show only their real `render`
 * by default. To fill sparse handoff bodies with the EVT-021 prototype fixture:
 *   localStorage.setItem('wq_enrich_handoff', '1')  — see handoffFixtures.js
 * Live break-glass rows with sparse render:
 *   localStorage.setItem('wq_enrich_break_glass', '1')  — see breakGlassFixtures.js
 *
 * Retire this opt-in seed (CT-4867) once LangGraph delivers enough HITL tasks in dev;
 * until then it stays for offline UI work without Platform auth.
 */

import { MOCK_EVENTS, SCOPE } from './mockEvents.js';
import { EVENT_TYPE } from './eventTypes.js';
import { DUE_BUCKET } from './filterOptions.js';

const HITL_TYPES = new Set([
  EVENT_TYPE.HTL_CONTINUATION,
  EVENT_TYPE.HTL_HANDOFF,
  EVENT_TYPE.QUESTION,
  EVENT_TYPE.GOV_BREAK_GLASS,
  EVENT_TYPE.CLIENT_CONTINUATION,
  EVENT_TYPE.CLIENT_HANDOFF,
]);

const HOUR = 60 * 60 * 1000;

/** Offset from now that puts a seeded row inside its declared bucket. */
const SEED_DUE_OFFSET_HOURS = {
  [DUE_BUCKET.DUE_TODAY]: 6,
  [DUE_BUCKET.THIS_WEEK]: 72,
  [DUE_BUCKET.LATER]: 480,
};

/**
 * A plausible `dueAt` for a seeded row, derived from the bucket it declares. The
 * mock rows predate `dueAt`, and the due-date sort orders on a real timestamp —
 * without this every seeded row would sort as dateless. `overdue` is a flag, not
 * a bucket, so it wins and pushes the row into the past.
 */
function seedDueAt(event) {
  if (event.overdue) return new Date(Date.now() - 4 * HOUR).toISOString();
  const hours = SEED_DUE_OFFSET_HOURS[event.dueBucket];
  return hours ? new Date(Date.now() + hours * HOUR).toISOString() : null;
}

/** True only in a dev build with the explicit opt-in flag set. */
function seedEnabled() {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem('wq_seed_hitl') === '1';
  } catch {
    return false;
  }
}

/**
 * The HITL rows from MOCK_EVENTS, forced to the My Work scope so they all show in
 * one tab, or `[]` when the seed is not enabled. Appended to the live aggregate in
 * {@link WorkQueueProvider} — they are already in the final UI-event shape.
 */
export function getDevSeedHitlEvents() {
  if (!seedEnabled()) return [];
  return MOCK_EVENTS.filter((event) => HITL_TYPES.has(event.type)).map((event) => ({
    ...event,
    scope: SCOPE.MY_WORK,
    dueAt: seedDueAt(event),
  }));
}
